import type { CSSProperties } from "react";
import type { Menu, Tenant } from "@/payload-types";
import type { LocaleCode } from "@/lib/labels/types";
import type { IconNode } from "@/components/ui/Icon";
import type { MenuItemWithIcon } from "@/utilities/icons/resolveMenuIcons";

/**
 * Client-safe header model. The server header resolves Payload documents (tenant,
 * menus populated at depth 2, mega-menu lists) into these slim shapes so the client
 * header components receive only the fields they render.
 */

export interface HeaderLink {
    label: string;
    url: string;
    openInNewTab: boolean;
}

/** One entry of a mega-menu competitions/teams list. */
export interface HeroListItem {
    entityId: string;
    label?: string | null;
    logo?: string | null;
}

export interface HeaderMegaMenu {
    competitions: HeroListItem[];
    teams: HeroListItem[];
}

export interface HeaderMenuItem extends HeaderLink {
    highlight: boolean;
    iconOnly: boolean;
    showOnMobile: boolean;
    iconNode: IconNode | null;
    children: HeaderLink[];
    /** Non-null when the item has a mega-menu competitions relation (populated or not). */
    megaMenu: HeaderMegaMenu | null;
}

export interface HeaderBrand {
    name: string;
    logoUrl: string | null;
    logoDarkUrl: string | null;
}

type RawMenuItem = NonNullable<Menu["items"]>[number] & { iconNode?: IconNode | null };

interface RawLink {
    label: string;
    url: string;
    openInNewTab?: boolean | null;
}

function toHeaderLink(item: RawLink): HeaderLink {
    return { label: item.label, url: item.url, openInNewTab: Boolean(item.openInNewTab) };
}

export function toHeaderLinks(items: readonly RawLink[] | null | undefined): HeaderLink[] {
    return (items ?? []).map(toHeaderLink);
}

/** Map a (possibly unpopulated) `List` relation to the entries the mega menu renders. */
export function toHeroListItems(list: unknown): HeroListItem[] {
    if (!list || typeof list !== "object") return [];
    const items = (list as { items?: unknown }).items;
    if (!Array.isArray(items)) return [];
    return items.map((entry: { entityId: string; label?: string | null; logo?: string | null }) => ({
        entityId: entry.entityId,
        label: entry.label ?? null,
        logo: entry.logo ?? null,
    }));
}

function toHeaderMenuItem(item: RawMenuItem): HeaderMenuItem {
    return {
        ...toHeaderLink(item),
        highlight: Boolean(item.highlight),
        iconOnly: Boolean(item.iconOnly),
        showOnMobile: Boolean(item.showOnMobile),
        iconNode: item.iconNode ?? null,
        children: toHeaderLinks(item.children),
        megaMenu: item.megaMenuCompetitions
            ? {
                  competitions: toHeroListItems(item.megaMenuCompetitions),
                  teams: toHeroListItems(item.megaMenuTeams),
              }
            : null,
    };
}

export function toHeaderMenuItems(items: MenuItemWithIcon[] | Menu["items"] | null | undefined): HeaderMenuItem[] {
    return (items ?? []).map(toHeaderMenuItem);
}

function mediaUrl(media: unknown): string | null {
    if (!media || typeof media !== "object") return null;
    return (media as { url?: string | null }).url ?? null;
}

export function toHeaderBrand(tenant: Tenant): HeaderBrand {
    return {
        name: tenant.name,
        logoUrl: mediaUrl(tenant.brand?.logo),
        logoDarkUrl: mediaUrl(tenant.brand?.logoDark),
    };
}

/**
 * Logo for the header surface: light text (dark background) prefers the dark-background logo.
 */
export function pickHeaderLogo(brand: HeaderBrand, textMode: "light" | "dark"): string | null {
    return textMode === "light" ? (brand.logoDarkUrl ?? brand.logoUrl) : brand.logoUrl;
}

export type BrandRowArrangement = "split" | "centered" | "evenly" | "spread";
export type BrandRowLogoSize = "sm" | "md" | "lg";

export interface HeaderBrandRowLogo {
    imageUrl: string;
    url: string;
    label: string;
    openInNewTab: boolean;
}

/** Desktop-only row of brand logos rendered above the header variant. */
export interface HeaderBrandRow {
    showMainLogo: boolean;
    logos: HeaderBrandRowLogo[];
    arrangement: BrandRowArrangement;
    mirror: boolean;
    mainLogoSize: BrandRowLogoSize;
    brandLogoSize: BrandRowLogoSize;
}

/**
 * Resolve the tenant's brand row. Null when disabled or when there is nothing to show;
 * logos whose media is unpopulated or has no url are dropped.
 */
export function toHeaderBrandRow(tenant: Tenant): HeaderBrandRow | null {
    const config = tenant.headerFooter?.header?.brandRow;
    if (!config?.enabled) return null;

    const logos: HeaderBrandRowLogo[] = [];
    for (const logo of config.logos ?? []) {
        const imageUrl = mediaUrl(logo.image);
        if (!imageUrl) continue;
        logos.push({
            imageUrl,
            url: logo.url,
            label: logo.label,
            openInNewTab: logo.openInNewTab !== false,
        });
    }

    const showMainLogo = Boolean(config.showMainLogo);
    if (!showMainLogo && logos.length === 0) return null;

    return {
        showMainLogo,
        logos,
        arrangement: config.arrangement ?? "split",
        mirror: Boolean(config.mirror),
        mainLogoSize: config.mainLogoSize ?? "md",
        brandLogoSize: config.brandLogoSize ?? "md",
    };
}

/** The header variant drops its desktop logo only when the brand row carries the main logo. */
export function shouldHideDesktopLogo(brandRow: HeaderBrandRow | null): boolean {
    return brandRow?.showMainLogo === true;
}

/** Pinning is opt-in per tenant and independent of the header variant. */
export function isStickyHeader(tenant: Tenant): boolean {
    return tenant.headerFooter?.header?.sticky === true;
}

/** Props shared by every header variant (`HeaderBranded*`). */
export interface HeaderVariantProps {
    brand: HeaderBrand;
    items: HeaderMenuItem[];
    iconItems: HeaderMenuItem[];
    enabledLocales: LocaleCode[];
    showLangSwitcher: boolean;
    basePath: string;
    showSearch?: boolean;
    textMode?: "light" | "dark";
    density?: string;
    styleOverrides?: CSSProperties;
    background?: string;
    /** Hide the logo at md+ because the brand row shows it; mobile keeps it. */
    hideDesktopLogo?: boolean;
    /** Tenant opted into a pinned header; variants without their own sticky class add it for mobile. */
    sticky?: boolean;
}
